/**
 * Route Transition Component
 * Smooth animations for route changes using Framer Motion
 * Priority 2.2.4: Route transition animations
 *
 * Deliberately NO AnimatePresence / exit animations here. Router navigations
 * run inside React's startTransition; an AnimatePresence exit caught in an
 * interrupted transition freezes mid-crossfade, leaving the old route as a
 * position:absolute layer stuck over the live one — the page looks rendered
 * but hovers/clicks/scroll hit the invisible layer underneath, while both
 * route trees stay mounted and keep polling (CPU + memory climb). Reproduced
 * live 2026-07-19 on /dashboard: two sibling layers frozen at opacity
 * 0.849/0.151 for 6+ seconds. Same class as the NotificationsDropdown fix
 * (c44bb5396) and #201. Enter-only animation cannot get into that state.
 */

import { m } from 'framer-motion';
import { ReactNode, useRef } from 'react';
import { useMotionPreferences } from './useMotionPreferences';

export interface RouteTransitionProps {
  children: ReactNode;
  /**
   * Unique key for the route. Must be the pathname the Outlet is actually
   * rendering (useRenderedPathname), NOT useLocation().pathname — the pending
   * location flips at navigation start while the content swaps later, which
   * would restart the animation on the OLD page.
   */
  routeKey: string;
  /**
   * Animation variant
   * - 'fade': Simple fade in
   * - 'slide': Slide from right with fade
   * - 'scale': Scale up with fade
   * - 'none': No animation (for accessibility/reduced motion)
   */
  variant?: 'fade' | 'slide' | 'scale' | 'none';
  /**
   * Animation duration in seconds
   */
  duration?: number;
}

/**
 * Enter-only animation variants for route transitions
 */
const variants = {
  fade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
  },
  slide: {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
  },
  scale: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
  },
  none: {
    initial: {},
    animate: {},
  },
};

/**
 * RouteTransition component wraps route content with a smooth enter animation.
 * The outgoing route unmounts synchronously with React — no exit layer exists.
 *
 * Usage:
 * ```tsx
 * <RouteTransition routeKey={renderedPathname} variant="fade">
 *   <YourRouteContent />
 * </RouteTransition>
 * ```
 */
export function RouteTransition({
  children,
  routeKey,
  variant = 'fade',
  duration = 0.2,
}: RouteTransitionProps) {
  const { shouldReduceMotion } = useMotionPreferences();
  const effectiveVariant = shouldReduceMotion ? 'none' : variant;
  const animationVariants = variants[effectiveVariant];

  // Skip the animation on first paint (parity with the old
  // AnimatePresence initial={false}) — only navigations animate.
  const hasNavigatedRef = useRef(false);
  const prevKeyRef = useRef(routeKey);
  if (prevKeyRef.current !== routeKey) {
    hasNavigatedRef.current = true;
    prevKeyRef.current = routeKey;
  }

  return (
    <m.div
      key={routeKey}
      initial={hasNavigatedRef.current ? animationVariants.initial : false}
      animate={animationVariants.animate}
      transition={{
        duration,
        ease: 'easeInOut',
      }}
      className="h-full w-full"
    >
      {children}
    </m.div>
  );
}

/**
 * Simplified RouteTransition for use in __root.tsx
 * Automatically respects reduced motion preferences
 */
export function SimpleRouteTransition({
  children,
  routeKey,
}: {
  children: ReactNode;
  routeKey: string;
}) {
  return (
    <RouteTransition routeKey={routeKey} variant="fade" duration={0.3}>
      {children}
    </RouteTransition>
  );
}
